import {
  type ExperimentAssignment,
  type ExperimentDailyActivity,
  type ExperimentJourneyEvent,
  type ExperimentMetrics,
  type ExperimentSummary,
  formatOptionalRevenue,
  formatRate,
  formatRevenue,
  type MonetizationOutcome,
} from "../experiments/experimentClient";
import type { MonetizationExperiment } from "./monetizationExperiments";

const META_AD_SURFACES = new Set(["website", "whatsapp", "launchpad"]);

/** The metrics block a read model treats as "the paygate": totals or one variant. */
export function headlineMetrics(
  summary: ExperimentSummary | undefined,
  experiment: MonetizationExperiment,
): ExperimentMetrics | undefined {
  if (!summary) return undefined;
  if (experiment.headline === "totals") return summary.totals;
  return summary.variants.find(
    (variant) => variant.variant === experiment.headline,
  );
}

export type ConversionStep = {
  label: string;
  count: number;
  nextRate: number | null;
};

/**
 * Contact funnel from assignment to payment. "Echo started" stands in for
 * delivery: the bot tags `echo.started` with the paygate assignment for every
 * contact, while `final_video.delivered` only started carrying it on
 * 2026-09-19, so a delivered count would under-read older contacts.
 */
export function buildConversionSteps(
  metrics: ExperimentMetrics,
): ConversionStep[] {
  const counts: [string, number][] = [
    ["Assigned", metrics.assigned_contacts ?? 0],
    ["Echo started", metrics.echo_started_contacts ?? 0],
    ["Rated", metrics.rated_contacts ?? 0],
    ["Offer sent", metrics.offer_prompted_contacts ?? 0],
    ["Package selected", metrics.offer_selected_contacts ?? 0],
    ["Checkout created", metrics.checkout_created_contacts ?? 0],
    ["Paid", metrics.paid_contacts ?? 0],
  ];
  return counts.map(([label, count], index) => ({
    label,
    count,
    nextRate:
      index + 1 < counts.length ? stepRate(counts[index + 1][1], count) : null,
  }));
}

/** Share of `from` contacts that reached `to`, as the API rounds it (1 dp). */
export function stepRate(to: number, from: number): number | null {
  if (!from) return null;
  return Math.round((1000 * to) / from) / 10;
}

export function offerLabel(offerId: string): string {
  // Since 2026-09-29 new contacts see the €1.99 single; contacts created before
  // the price-book cutoff keep the €0.99 intro single.
  if (offerId === "echo-single-eur-199-v1") return "1 Echo · €1.99";
  if (offerId === "echo-single-eur-intro-99-v1")
    return "1 Echo · €0.99 (existing contacts)";
  if (offerId === "echo-single-eur-v1") return "1 Echo · €1.85";
  if (offerId === "echo-pack-5-eur-v1") return "5 Echoes · €7.50";
  return offerId;
}

export function isVerifiedMetaAdAssignment(
  assignment: Pick<
    ExperimentAssignment,
    | "eligibility_match"
    | "acquisition_group"
    | "acquisition_platform"
    | "acquisition_surface"
  >,
): boolean {
  return (
    assignment.eligibility_match === true &&
    assignment.acquisition_group === "paid" &&
    assignment.acquisition_platform === "meta" &&
    META_AD_SURFACES.has(assignment.acquisition_surface ?? "")
  );
}

const OUTCOME_LABELS: Record<MonetizationOutcome, string> = {
  before_offer: "Before offer",
  offer_open: "No purchase yet",
  offer_selected: "Package selected",
  checkout_open: "Checkout open",
  // The customer opened a checkout and never paid; Stripe expired the
  // session. Not a failure on our side and not a refusal by them.
  checkout_expired: "Cart abandoned",
  payment_failed: "Payment failed",
  paid: "Paid",
  refunded: "Refunded",
  disputed: "Disputed",
};

const STAGE_LABELS: Record<string, string> = {
  assigned: "Assigned",
  echo_started: "Echo started",
  echo_delivered: "First Echo delivered",
  rated: "Rated",
  offer_sent: "Offer sent",
  offer_selected: "Package selected",
  checkout_created: "Checkout created",
  paid: "Paid",
};

const EVENT_LABELS: Record<string, string> = {
  "experiment.assigned": "Assigned to experiment",
  "experiment.exposed": "Experiment applied",
  "echo.started": "Echo started",
  "final_video.delivered": "Echo delivered",
  "feedback.rating_received": "Rating received",
  "feedback.received": "Rating received",
  "monetization.offer_prompted": "Offer sent",
  "monetization.offer_selected": "Package selected",
  "commerce.checkout_created": "Checkout created",
  "access.paywall_shown": "Paywall shown",
  "commerce.payment_succeeded": "Payment succeeded",
  "commerce.payment_failed": "Payment failed",
  "commerce.payment_expired": "Cart abandoned (checkout link expired)",
  "commerce.cart_recovery_prompted": "Abandoned-cart reminder sent",
  "commerce.payment_refunded": "Payment refunded",
  "commerce.payment_partially_refunded": "Payment partially refunded",
  "commerce.payment_disputed": "Payment disputed",
  "commerce.payment_dispute_won": "Dispute won",
  "referral.qualified": "Referred friend's first Echo delivered",
  "referral.reward_granted": "Referral credit earned",
  "referral.reward_skipped": "Referral credit skipped",
  "referral.notified": "Referral credit notified",
};

export function outcomeLabel(outcome?: string | null): string {
  if (!outcome) return "Unknown";
  return OUTCOME_LABELS[outcome as MonetizationOutcome] ?? outcome;
}

export function stageLabel(stage?: string | null): string {
  if (!stage) return "Unknown";
  return STAGE_LABELS[stage] ?? stage;
}

export function journeyEventLabel(eventType: string): string {
  return EVENT_LABELS[eventType] ?? eventType;
}

export function journeyEventDetail(
  event: ExperimentJourneyEvent,
): string | null {
  if (
    event.event_type === "feedback.rating_received" ||
    event.event_type === "feedback.received"
  ) {
    return event.rating == null
      ? null
      : `${event.rating} star${event.rating === 1 ? "" : "s"}`;
  }
  if (event.offer_id) return offerLabel(event.offer_id);
  if (event.offer_ids?.length) {
    return event.offer_ids.map(offerLabel).join(" or ");
  }
  return null;
}

export function formatDateTime(value?: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

/** A `YYYY-MM-DD` UTC day from the daily series, shown as a date only. */
export function formatDay(day: string): string {
  const date = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return day;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeZone: "UTC",
  }).format(date);
}

export function formatMinutes(minutes?: number | null): string {
  if (minutes == null) return "—";
  if (minutes < 1) return "<1 min";
  if (minutes < 90) return `${Math.round(minutes)} min`;
  if (minutes < 48 * 60) return `${(minutes / 60).toFixed(1)} h`;
  return `${(minutes / (24 * 60)).toFixed(1)} d`;
}

export function formatRatio(value?: number | null): string {
  if (value == null) return "—";
  return String(Number(value.toFixed(2)));
}

export type KpiItem = {
  label: string;
  value: string;
  hint?: string;
};

const count = (value?: number | null): number => value ?? 0;

/** Headline tiles: exposure, buyers, orders and money, in one row of facts. */
export function buildOverviewTiles(metrics: ExperimentMetrics): KpiItem[] {
  const refunded = count(metrics.refunded_minor);
  return [
    {
      label: "Contacts under paygate",
      value: String(count(metrics.assigned_contacts)),
      hint: [
        `${count(metrics.echo_started_contacts)} started an Echo`,
        count(metrics.echo_delivered_contacts)
          ? `${count(metrics.echo_delivered_contacts)} delivered`
          : null,
      ]
        .filter(Boolean)
        .join(" · "),
    },
    {
      label: "Hit the hard paywall",
      value: String(count(metrics.paywall_contacts)),
      hint: `${count(metrics.paywall_views)} hits in total · ${formatRatio(
        metrics.paywall_views_per_paywalled_contact,
      )} per contact`,
    },
    {
      label: "Offers sent",
      value: String(count(metrics.offer_prompted_contacts)),
      hint: "contacts shown the post-rating offer",
    },
    {
      label: "Buyers",
      value: String(count(metrics.paid_contacts)),
      hint: `${count(metrics.repeat_paid_contacts)} bought more than once`,
    },
    {
      label: "Paid orders",
      value: String(count(metrics.paid_orders)),
      hint: `${count(metrics.credits_granted)} Echoes sold`,
    },
    {
      label: "Net revenue",
      value: formatRevenue(count(metrics.net_revenue_minor), metrics.currency),
      hint: refunded
        ? `${formatRevenue(refunded, metrics.currency)} refunded`
        : "no refunds",
    },
    {
      label: "Contact → paid",
      value: formatRate(metrics.assignment_to_paid_pct ?? null),
      hint: `${count(metrics.paid_contacts)} of ${count(metrics.assigned_contacts)} contacts`,
    },
    {
      label: "Last sale",
      value: formatDateTime(metrics.last_payment_at),
      hint: metrics.first_payment_at
        ? `first ${formatDateTime(metrics.first_payment_at)}`
        : "no sale yet",
    },
  ];
}

export function buildSalesKpis(metrics: ExperimentMetrics): KpiItem[] {
  const currency = metrics.currency;
  return [
    {
      label: "Average order value",
      value: formatOptionalRevenue(metrics.average_order_value_minor, currency),
      hint: "gross, per paid order",
    },
    {
      label: "Revenue per buyer",
      value: formatOptionalRevenue(
        metrics.net_revenue_per_paid_contact_minor,
        currency,
      ),
      hint: "net, lifetime so far",
    },
    {
      label: "Revenue per contact",
      value: formatOptionalRevenue(
        metrics.net_revenue_per_assigned_contact_minor,
        currency,
      ),
      hint: "net, across every contact under the paygate",
    },
    {
      label: "Orders per buyer",
      value: formatRatio(metrics.orders_per_paid_contact),
      hint: `${count(metrics.paid_orders)} orders / ${count(metrics.paid_contacts)} buyers`,
    },
    {
      label: "Median time to pay",
      value: formatMinutes(metrics.median_minutes_to_first_payment),
      hint: "from the last paywall or offer shown",
    },
    {
      label: "Echoes sold",
      value: String(count(metrics.credits_granted)),
      hint: `${count(metrics.internal_credits_granted)} internal credits`,
    },
  ];
}

export function buildConversionKpis(metrics: ExperimentMetrics): KpiItem[] {
  return [
    {
      label: "Contact → paid",
      value: formatRate(metrics.assignment_to_paid_pct ?? null),
      hint: `${count(metrics.paid_contacts)} / ${count(metrics.assigned_contacts)}`,
    },
    {
      label: "Paywall → paid",
      value: formatRate(metrics.paywall_to_paid_pct ?? null),
      hint: `${count(metrics.paywall_contacts)} contacts hit the hard paywall`,
    },
    {
      label: "Offer → paid",
      value: formatRate(metrics.prompt_to_paid_pct ?? null),
      hint: `${count(metrics.offer_prompted_contacts)} contacts got the soft offer`,
    },
    {
      label: "Selected → checkout",
      value: formatRate(metrics.selection_to_checkout_pct ?? null),
      hint: `${count(metrics.checkout_created_contacts)} / ${count(metrics.offer_selected_contacts)}`,
    },
    {
      label: "Checkout → paid",
      value: formatRate(metrics.checkout_to_paid_pct ?? null),
      hint: "Stripe form completion",
    },
    {
      label: "Carts abandoned",
      value: formatRate(metrics.checkout_abandoned_pct ?? null),
      hint: `${count(metrics.cart_abandoned_contacts)} contacts opened a checkout and never paid`,
    },
    {
      label: "Reminder → paid",
      value: formatRate(metrics.cart_recovery_to_paid_pct ?? null),
      hint: `${count(metrics.cart_recovery_prompted_contacts)} contacts reminded while the link was live`,
    },
    {
      label: "Rated → offer",
      value: formatRate(metrics.rating_to_prompt_pct ?? null),
      hint: `${count(metrics.rated_contacts)} contacts rated`,
    },
  ];
}

export function buildRetentionKpis(metrics: ExperimentMetrics): KpiItem[] {
  return [
    {
      label: "Repeat purchase rate",
      value: formatRate(metrics.repeat_purchase_pct ?? null),
      hint: `${count(metrics.repeat_paid_contacts)} of ${count(metrics.paid_contacts)} buyers bought again`,
    },
    {
      label: "Created after buying",
      value: formatRate(metrics.post_purchase_activation_pct ?? null),
      hint: `${count(metrics.post_purchase_echo_started_contacts)} buyers started another Echo`,
    },
    {
      label: "Paywall hits per blocked contact",
      value: formatRatio(metrics.paywall_views_per_paywalled_contact),
      hint: "how often a blocked contact keeps trying",
    },
    {
      label: "Referral credits earned",
      value: String(count(metrics.referral_rewards_granted)),
      hint: `${count(metrics.referral_rewarded_contacts)} contacts rewarded`,
    },
    {
      label: "Paywalled → referred",
      value: formatRate(metrics.paywall_to_referral_pct ?? null),
      hint: "blocked contacts who earned a referral credit",
    },
    {
      label: "Five-star contacts",
      value: String(count(metrics.five_star_contacts)),
      hint: `${formatRate(metrics.five_star_to_prompt_pct ?? null)} of them got the offer`,
    },
  ];
}

const DASHBOARD_SALES_TILE_LABELS = [
  "Paid orders",
  "Net revenue",
  "Referral credits earned",
  "Repeat purchase rate",
] as const;

/**
 * The four sales facts the CRM home shows: the same items, same copy, as the
 * Monetization overview and retention cards, so the two pages never disagree.
 */
export function buildDashboardSalesTiles(
  metrics: ExperimentMetrics,
): KpiItem[] {
  const items = [
    ...buildOverviewTiles(metrics),
    ...buildRetentionKpis(metrics),
  ];
  return DASHBOARD_SALES_TILE_LABELS.map(
    (label) => items.find((item) => item.label === label) as KpiItem,
  );
}

export type PaygateTriggerRow = {
  key: "paywall" | "offer" | "recovery" | "unattributed";
  label: string;
  description: string;
  contacts: number | null;
  exposures: number | null;
  paidOrders: number;
  toPaidPct: number | null;
};

/**
 * The two surfaces that put a price in front of a contact, with the paid
 * orders the backend attributed to the latest one shown before the purchase.
 */
export function buildPaygateTriggerRows(
  metrics: ExperimentMetrics,
  daily?: ExperimentDailyActivity[],
): PaygateTriggerRow[] {
  const offersSent = daily
    ? daily.reduce((sum, day) => sum + count(day.offers_prompted), 0)
    : null;
  const rows: PaygateTriggerRow[] = [
    {
      key: "paywall",
      label: "Hard paywall",
      description: "Create blocked until a purchase or referral credit",
      contacts: count(metrics.paywall_contacts),
      exposures: count(metrics.paywall_views),
      paidOrders: count(metrics.paid_orders_after_paywall),
      toPaidPct: metrics.paywall_to_paid_pct ?? null,
    },
    {
      key: "offer",
      label: "Soft offer",
      description: "Post-rating upsell while credits remain",
      contacts: count(metrics.offer_prompted_contacts),
      exposures: offersSent,
      paidOrders: count(metrics.paid_orders_after_offer),
      toPaidPct: metrics.prompt_to_paid_pct ?? null,
    },
    {
      key: "recovery",
      label: "Abandoned-cart reminder",
      description: "One nudge, sent while the checkout link is still live",
      contacts: count(metrics.cart_recovery_prompted_contacts),
      exposures: count(metrics.cart_recovery_prompts),
      paidOrders: count(metrics.paid_orders_after_recovery),
      toPaidPct: metrics.cart_recovery_to_paid_pct ?? null,
    },
  ];
  if (count(metrics.paid_orders_unattributed) > 0) {
    rows.push({
      key: "unattributed",
      label: "No gate recorded",
      description: "Paid without a paywall or offer fact before selection",
      contacts: null,
      exposures: null,
      paidOrders: count(metrics.paid_orders_unattributed),
      toPaidPct: null,
    });
  }
  return rows;
}

export type EligibilityKind = "verified" | "policy" | "mismatch";

/**
 * How a contact came to be assigned. The Meta-ads A/B needed paid Meta
 * evidence; the contact paywall admits every B2C contact by policy.
 */
export function eligibilityKind(
  assignment: Parameters<typeof isVerifiedMetaAdAssignment>[0],
): EligibilityKind {
  if (isVerifiedMetaAdAssignment(assignment)) return "verified";
  return assignment.eligibility_match === true ? "policy" : "mismatch";
}

const EMPTY_DAY: Omit<ExperimentDailyActivity, "date"> = {
  assigned_contacts: 0,
  echoes_started: 0,
  paywall_views: 0,
  paywall_contacts: 0,
  offers_prompted: 0,
  offer_contacts: 0,
  checkouts_created: 0,
  carts_abandoned: 0,
  cart_recovery_prompts: 0,
  paid_orders: 0,
  paid_contacts: 0,
  revenue_minor: 0,
  referral_rewards_granted: 0,
};

/**
 * The last `days` UTC days ending today, newest first and zero-filled, so a
 * quiet day reads as a quiet day rather than disappearing from the table.
 */
export function recentDailyActivity(
  daily: ExperimentDailyActivity[],
  days: number,
  today: Date = new Date(),
): ExperimentDailyActivity[] {
  const byDate = new Map(daily.map((day) => [day.date, day]));
  const end = Date.UTC(
    today.getUTCFullYear(),
    today.getUTCMonth(),
    today.getUTCDate(),
  );
  return Array.from({ length: days }, (_, index) => {
    const date = new Date(end - index * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 10);
    return byDate.get(date) ?? { date, ...EMPTY_DAY };
  });
}

export function sortDailyNewestFirst(
  daily: ExperimentDailyActivity[],
): ExperimentDailyActivity[] {
  return [...daily].sort((a, b) => (a.date < b.date ? 1 : -1));
}
